import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip } from "chart.js";
import { Doughnut } from "react-chartjs-2";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import api from "api/axios";
import CarteStat, { ombreLegere } from "components/SacrificesCarteStat";
import {
  statutLabel, statutColor, typeAnimalLabel, causeLabel, fmtEuro, fmtDate,
} from "components/sacrificesConfig";

ChartJS.register(ArcElement, ChartTooltip);

// Palette catégorielle (slots 1 à 4 de la palette de référence, validée daltonisme).
// Volontairement distincte des couleurs de statut, réservées aux statuts.
// Aqua et jaune ont un contraste < 3:1 sur blanc : chaque graphique a donc une légende chiffrée visible.
const SERIES = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100"];

const statutIcon = {
  en_attente: "hourglass_empty",
  validee_local: "verified",
  mandat_envoye: "send",
  mandats_acceptes: "task_alt",
};

const COULEUR_TEXTE = "#344767";
const COULEUR_TEXTE_SECONDAIRE = "#7b809a";
const pct = (n, total) => (total > 0 ? Math.round((n / total) * 100) : 0);

// Barre de progression : piste claire + remplissage de la même teinte
function Barre({ valeur, color }) {
  return (
    <MDBox sx={{ height: 6, borderRadius: 3, bgcolor: color + "22", overflow: "hidden" }}>
      <MDBox sx={{ height: "100%", width: valeur + "%", bgcolor: color, borderRadius: 3, transition: "width 0.4s" }} />
    </MDBox>
  );
}

Barre.propTypes = { valeur: PropTypes.number.isRequired, color: PropTypes.string.isRequired };

// Carte "anneau" : graphique + légende chiffrée (libellé, valeur, %) qui sert aussi de vue tableau
function CarteAnneau({ titre, sousTitre, unite, donnees }) {
  const total = donnees.reduce((t, d) => t + d.valeur, 0);
  const data = {
    labels: donnees.map((d) => d.label),
    datasets: [{
      data: donnees.map((d) => d.valeur),
      backgroundColor: donnees.map((d, i) => SERIES[i]),
      borderColor: "#ffffff",
      borderWidth: 2,
      borderRadius: 4,
      hoverOffset: 6,
    }],
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "64%",
    layout: { padding: 6 },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#ffffff",
        titleColor: COULEUR_TEXTE,
        bodyColor: COULEUR_TEXTE,
        borderColor: "#e0e0e0",
        borderWidth: 1,
        padding: 10,
        boxPadding: 4,
        callbacks: {
          label: (ctx) => ` ${ctx.raw} ${unite} (${pct(ctx.raw, total)} %)`,
        },
      },
    },
  };

  return (
    <Card sx={{ boxShadow: ombreLegere, height: "100%" }}>
      <MDBox px={2.5} pt={2.5}>
        <MDTypography variant="h6">{titre}</MDTypography>
        <MDTypography variant="caption" color="text">{sousTitre}</MDTypography>
      </MDBox>
      {total === 0 ? (
        <MDBox py={6} textAlign="center">
          <Icon sx={{ fontSize: "2.5rem !important", color: "#c4c9d4" }}>donut_large</Icon>
          <MDTypography variant="body2" color="text" display="block">Aucune donnée</MDTypography>
        </MDBox>
      ) : (
        <MDBox display="flex" flexDirection={{ xs: "column", sm: "row" }} alignItems="center" gap={2} p={2.5}>
          <MDBox position="relative" sx={{ width: 200, height: 200, flexShrink: 0 }}>
            <Doughnut data={data} options={options} />
            <MDBox
              position="absolute" top={0} left={0} right={0} bottom={0}
              display="flex" flexDirection="column" alignItems="center" justifyContent="center"
              sx={{ pointerEvents: "none" }}
            >
              <MDTypography variant="h4" fontWeight="bold" sx={{ color: COULEUR_TEXTE, lineHeight: 1 }}>{total}</MDTypography>
              <MDTypography variant="caption" sx={{ color: COULEUR_TEXTE_SECONDAIRE }}>{unite}</MDTypography>
            </MDBox>
          </MDBox>
          <MDBox flex={1} width="100%">
            {donnees.map((d, i) => (
              <MDBox
                key={d.label} display="flex" alignItems="center" gap={1.25} py={1}
                sx={{ borderBottom: "1px solid #f0f0f0", "&:last-child": { borderBottom: "none" } }}
              >
                <MDBox sx={{ width: 12, height: 12, borderRadius: "3px", bgcolor: SERIES[i], flexShrink: 0 }} />
                <MDTypography variant="button" fontWeight="regular" sx={{ color: COULEUR_TEXTE, flex: 1 }}>
                  {d.label}
                </MDTypography>
                <MDTypography variant="button" fontWeight="bold" sx={{ color: COULEUR_TEXTE }}>{d.valeur}</MDTypography>
                <MDTypography variant="caption" sx={{ color: COULEUR_TEXTE_SECONDAIRE, width: 40, textAlign: "right" }}>
                  {pct(d.valeur, total)} %
                </MDTypography>
              </MDBox>
            ))}
          </MDBox>
        </MDBox>
      )}
    </Card>
  );
}

CarteAnneau.propTypes = {
  titre: PropTypes.string.isRequired,
  sousTitre: PropTypes.string.isRequired,
  unite: PropTypes.string.isRequired,
  donnees: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    valeur: PropTypes.number,
  })).isRequired,
};

const styleEnteteColonne = {
  fontSize: "11px",
  fontWeight: 700,
  color: "#546e7a",
  textTransform: "uppercase",
  letterSpacing: "0.6px",
  whiteSpace: "nowrap",
};
const ellipsis = { display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };

// Colonnes du top villes en largeurs relatives (pas de défilement horizontal) ;
// "des" = point de rupture à partir duquel la colonne s'affiche
const colonnesVilles = [
  { cle: "ville", label: "Ville", poids: 18 },
  { cle: "directeur", label: "Directeur", poids: 22, des: "md" },
  { cle: "demandes", label: "Demandes", poids: 12, align: "center" },
  { cle: "animaux", label: "Animaux", poids: 12, align: "center", des: "sm" },
  { cle: "montant", label: "Montant", poids: 15, align: "right", des: "sm" },
  { cle: "statut", label: "Statut majoritaire", poids: 21, des: "lg" },
];
const styleCellule = (c) => ({
  width: c.poids + "%",
  px: 1.25,
  display: c.des ? { xs: "none", [c.des]: "table-cell" } : "table-cell",
});

function SacrificesDashboard() {
  const [donnees, setDonnees] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState("");

  const fetchDashboard = async () => {
    setChargement(true);
    setErreur("");
    try {
      const res = await api.get("/sacrifices/dashboard");
      setDonnees(res.data);
    } catch (err) {
      setErreur(err.response?.data?.message || "Impossible de charger le tableau de bord");
    } finally {
      setChargement(false);
    }
  };

  useEffect(() => { fetchDashboard(); }, []);

  const stats = donnees?.stats;
  const totalDemandes = stats?.nb_demandes || 0;

  const cellule = (cle, contenu) => {
    const c = colonnesVilles.find((x) => x.cle === cle);
    return (
      <TableCell align={c.align || "left"} sx={styleCellule(c)}>
        {typeof contenu === "string" || typeof contenu === "number" ? (
          <MDTypography variant="button" fontWeight="regular" title={String(contenu)} sx={{ ...ellipsis, color: COULEUR_TEXTE }}>
            {contenu}
          </MDTypography>
        ) : contenu}
      </TableCell>
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card sx={{ boxShadow: ombreLegere, mb: 3 }}>
          <MDBox
            mx={2} mt={-3} py={3} px={2}
            variant="gradient" bgColor="info"
            borderRadius="lg" coloredShadow="info"
            display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}
          >
            <MDBox display="flex" alignItems="center" gap={1.5}>
              <Icon sx={{ color: "white" }} fontSize="medium">dashboard</Icon>
              <MDBox>
                <MDTypography variant="h6" color="white">Dashboard Sacrifices</MDTypography>
                <MDTypography variant="caption" color="white" sx={{ opacity: 0.9 }}>
                  {donnees?.session
                    ? `${donnees.session.libelle} · du ${fmtDate(donnees.session.date_debut)} au ${fmtDate(donnees.session.date_fin)}`
                    : "Aucune session active — toutes sessions confondues"}
                </MDTypography>
              </MDBox>
            </MDBox>
            <MDButton variant="outlined" color="white" size="small" onClick={fetchDashboard} disabled={chargement}>
              <Icon>refresh</Icon>&nbsp;Actualiser
            </MDButton>
          </MDBox>
          <MDBox p={2} />
        </Card>

        {erreur && (
          <MDBox mb={3} p={2} sx={{ bgcolor: "#fdecea", borderRadius: "8px", border: "1px solid #f5c2c0" }}>
            <MDTypography variant="button" sx={{ color: "#b71c1c" }}>{erreur}</MDTypography>
          </MDBox>
        )}

        {/* Ligne 1 : chiffres clés */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} sm={6} lg={3}>
            <CarteStat icon="assignment" label="Total demandes" valeur={stats ? stats.nb_demandes : "—"} color="#1976d2" />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <CarteStat icon="pets" label="Total animaux" valeur={stats ? stats.nb_animaux : "—"} color="#7b1fa2" />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <CarteStat icon="euro" label="Montant total" valeur={stats ? fmtEuro(stats.montant_total) : "—"} color="#43a047" />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <CarteStat icon="videocam" label="Demandes avec vidéo" valeur={stats ? stats.nb_videos : "—"} color="#fb8c00" />
          </Grid>
        </Grid>

        {/* Ligne 2 : répartition par statut */}
        <MDTypography variant="h6" mb={1.5}>Répartition par statut</MDTypography>
        <Grid container spacing={3} mb={3}>
          {(donnees?.par_statut || Object.keys(statutLabel).map((cle) => ({ cle, nb_demandes: 0 }))).map((st) => {
            const color = statutColor[st.cle];
            const p = pct(st.nb_demandes, totalDemandes);
            return (
              <Grid item xs={12} sm={6} lg={3} key={st.cle}>
                <Card sx={{ boxShadow: ombreLegere, borderTop: "4px solid " + color, height: "100%" }}>
                  <MDBox p={2}>
                    <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                      <Icon sx={{ color }}>{statutIcon[st.cle]}</Icon>
                      <MDTypography variant="button" fontWeight="medium" sx={{ color: COULEUR_TEXTE }}>
                        {statutLabel[st.cle]}
                      </MDTypography>
                    </MDBox>
                    <MDBox display="flex" alignItems="baseline" justifyContent="space-between" mb={1}>
                      <MDTypography variant="h3" fontWeight="bold" sx={{ color: COULEUR_TEXTE }}>{st.nb_demandes}</MDTypography>
                      <MDTypography variant="button" fontWeight="bold" sx={{ color: COULEUR_TEXTE_SECONDAIRE }}>{p} %</MDTypography>
                    </MDBox>
                    <Barre valeur={p} color={color} />
                  </MDBox>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Ligne 3 : graphiques */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} lg={6}>
            <CarteAnneau
              titre="Par type d'animal"
              sousTitre="Répartition en nombre d'animaux"
              unite="animaux"
              donnees={(donnees?.par_type_animal || []).map((d) => ({ label: typeAnimalLabel[d.cle], valeur: d.nb_animaux }))}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <CarteAnneau
              titre="Par cause"
              sousTitre="Répartition en nombre de demandes"
              unite="demandes"
              donnees={(donnees?.par_cause || []).map((d) => ({ label: causeLabel[d.cle], valeur: d.nb_demandes }))}
            />
          </Grid>
        </Grid>

        {/* Ligne 4 : top villes */}
        <Card sx={{ boxShadow: ombreLegere }}>
          <MDBox px={2.5} pt={2.5} pb={1} display="flex" justifyContent="space-between" alignItems="baseline" flexWrap="wrap" gap={1}>
            <MDTypography variant="h6">Top villes</MDTypography>
            <MDTypography variant="caption" color="text">
              Triées par nombre de demandes
            </MDTypography>
          </MDBox>
          <MDBox px={{ xs: 1, md: 2 }} pb={2}>
            <Table sx={{ width: "100%", tableLayout: "fixed" }}>
              <MDBox component="thead" sx={{ bgcolor: "#f8f9fa" }}>
                <TableRow>
                  {colonnesVilles.map((c) => (
                    <TableCell key={c.cle} align={c.align || "left"} sx={{ ...styleCellule(c), py: 1.25, borderBottom: "none" }}>
                      <MDBox component="span" title={c.label} sx={{ ...styleEnteteColonne, ...ellipsis }}>{c.label}</MDBox>
                    </TableCell>
                  ))}
                </TableRow>
              </MDBox>
              <TableBody>
                {(donnees?.top_villes || []).map((v, index) => {
                  return (
                    <TableRow key={v.ville} sx={{ "& td": { borderBottom: "1px solid #f0f0f0" }, "&:hover": { bgcolor: "#f5f9ff" } }}>
                      {cellule(
                        "ville",
                        <MDBox display="flex" alignItems="center" gap={1} minWidth={0}>
                          <MDTypography variant="caption" fontWeight="bold" sx={{ color: COULEUR_TEXTE_SECONDAIRE, flexShrink: 0 }}>
                            {index + 1}.
                          </MDTypography>
                          <MDTypography variant="button" fontWeight="bold" title={v.ville} sx={{ ...ellipsis, color: COULEUR_TEXTE }}>
                            {v.ville}
                          </MDTypography>
                        </MDBox>
                      )}
                      {cellule("directeur", v.directeur || "—")}
                      {cellule("demandes", v.nb_demandes)}
                      {cellule("animaux", v.nb_animaux)}
                      {cellule(
                        "montant",
                        <MDTypography variant="button" fontWeight="bold" sx={{ ...ellipsis, color: COULEUR_TEXTE }}>
                          {fmtEuro(v.montant_total)}
                        </MDTypography>
                      )}
                      {cellule(
                        "statut",
                        <Chip
                          label={statutLabel[v.statut_majoritaire]} size="small"
                          sx={{
                            bgcolor: statutColor[v.statut_majoritaire], color: "white", fontWeight: "bold",
                            borderRadius: "999px", maxWidth: "100%",
                          }}
                        />
                      )}
                    </TableRow>
                  );
                })}
                {!chargement && (donnees?.top_villes || []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={colonnesVilles.length}>
                      <MDBox py={4} textAlign="center">
                        <Icon sx={{ fontSize: "2.5rem !important", color: "#c4c9d4" }}>location_city</Icon>
                        <MDTypography variant="body2" color="text" display="block">Aucune demande pour le moment.</MDTypography>
                      </MDBox>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </MDBox>
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default SacrificesDashboard;
